import * as THREE from "three";
import type { DebugMapState, Mesh, Point } from "./types";

const R_MAPLIBRE = 6_371_008.8;

const mercatorOf = ([lng, lat]: [number, number]): { x: number; y: number } => ({
	x: (lng + 180) / 360,
	y: 0.5 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / (2 * Math.PI),
});

function toVector3(point: NonNullable<Point>): THREE.Vector3 {
	if (point.X === undefined || point.Y === undefined || point.Z === undefined) {
		console.error("Can't create THREE.Vector3 - incorrect Point shape", { point });
		return new THREE.Vector3();
	}
	return new THREE.Vector3(point.X, point.Z, -point.Y);
}

function clearGroup(group: THREE.Group) {
	while (group.children.length > 0) {
		const child = group.children.pop();

		if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
			child.geometry.dispose();

			const materials = Array.isArray(child.material) ? child.material : [child.material];
			materials.forEach((m) => {
				if ("map" in m && m.map instanceof THREE.Texture) {
					m.map.dispose();
				}
				m.dispose();
			});
		}
	}
}

function createGroundPlane({
	screenshotDataUrl,
	mapState,
	projectCenter,
}: {
	screenshotDataUrl: string;
	mapState: DebugMapState;
	projectCenter: [number, number];
}): THREE.Mesh {
	const mapMerc = mercatorOf(mapState.center);
	const projMerc = mercatorOf(projectCenter);

	const cosLat = Math.cos((projectCenter[1] * Math.PI) / 180);
	const circumferenceAtLat = 2 * Math.PI * R_MAPLIBRE * cosLat;

	// Offset map center vs project center in real meters at project latitude.
	// PayloadViewer scene_z is south-positive (matches MapLibre mercator_y), no sign flip.
	const sceneX = (mapMerc.x - projMerc.x) * circumferenceAtLat;
	const sceneZ = (mapMerc.y - projMerc.y) * circumferenceAtLat;

	const groundMPerPx = circumferenceAtLat / (512 * 2 ** mapState.zoom);
	const widthM = mapState.canvasSize.width * groundMPerPx;
	const heightM = mapState.canvasSize.height * groundMPerPx;

	const material = new THREE.MeshBasicMaterial({
		side: THREE.DoubleSide,
		depthWrite: false,
	});

	const geometry = new THREE.PlaneGeometry(widthM, heightM);
	geometry.rotateX(-Math.PI / 2);

	const mesh = new THREE.Mesh(geometry, material);
	mesh.position.set(sceneX, -0.05, sceneZ);
	mesh.renderOrder = -1;
	mesh.visible = false;

	new THREE.TextureLoader().load(screenshotDataUrl, (texture) => {
		texture.colorSpace = THREE.SRGBColorSpace;
		material.map = texture;
		material.needsUpdate = true;
		mesh.visible = true;
	});

	return mesh;
}

function createPolygon(points: Array<Point> | undefined, color: number, opacity: number) {
	if (points === undefined || points.length < 3) {
		return null;
	}

	const shape = new THREE.Shape(points.map((p) => new THREE.Vector2(p.X, p.Y)));
	const geometry = new THREE.ShapeGeometry(shape);
	geometry.rotateX(-Math.PI / 2);

	const material = new THREE.MeshBasicMaterial({
		color,
		transparent: true,
		opacity,
		side: THREE.DoubleSide,
		depthWrite: false,
	});

	return new THREE.Mesh(geometry, material);
}

function createLine(points: Array<Point> | undefined, color: number) {
	if (points === undefined || points.length < 2) {
		return null;
	}

	const geometry = new THREE.BufferGeometry().setFromPoints(points.map(toVector3));

	return new THREE.Line(geometry, new THREE.LineBasicMaterial({ color }));
}

function createMesh(mesh: Mesh | undefined, color: number) {
	const vertices = mesh?.Vertices ?? null;
	const indices = mesh?.Indices ?? null;

	if (vertices === null || indices === null) {
		return null;
	}

	const positions = new Float32Array(vertices.length * 3);
	vertices.forEach((v, i) => {
		const { X = 0, Y = 0, Z = 0 } = v;
		positions[i * 3] = X;
		positions[i * 3 + 1] = Z;
		positions[i * 3 + 2] = -Y;
	});

	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.setIndex(indices);
	geometry.computeVertexNormals();

	const material = new THREE.MeshLambertMaterial({
		color,
		transparent: true,
		opacity: 0.85,
		side: THREE.DoubleSide,
	});

	return new THREE.Mesh(geometry, material);
}

export { clearGroup, createGroundPlane, createLine, createMesh, createPolygon, toVector3 };
